import React, { useState, useMemo, useEffect } from "react";
import { Trans } from "@lingui/macro";
import { AlertCircle, Users, User } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { PayerSplitEditor } from "./PayerSplitEditor";
import {
  validatePayerPercentages,
  type ContractFamilyGroupDraft,
  type FamilyGroupPickerItem,
  type PayerRequest,
} from "@/types/billing";
import type { Contact } from "@/types/contact";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useChildRelationships } from "@/hooks/useChildContactRelationships";
import { RelationshipType } from "@/types/childContactRelationship";
import { FamilyAccountChooser } from "./FamilyAccountChooser";
import { areFamilyGroupDraftsEqual } from "./familyGroupDraft";
import { FamilyGroupSettingsFields } from "./FamilyGroupSettingsFields";
import { FamilyMembersCard } from "./FamilyMembersCard";

// Family billing types
type BillingMode = "family" | "individual";
type FamilyBillingType = "UNIFIED" | "SPLIT";

type FamilyGroup = ContractFamilyGroupDraft;
type FamilyMember = FamilyGroup["members"][number];

interface ContractWizardStepPayersProps {
  payers: PayerRequest[];
  setPayers: (payers: PayerRequest[]) => void;
  contactsData?: Contact[];
  childId?: string;
  _childFirstname?: string;
  childLastname?: string;
  familyGroup?: FamilyGroup;
  setFamilyGroup?: (group: FamilyGroup | undefined) => void;
  showHeader?: boolean;
  layout?: "stacked" | "grid"; // stacked = vertical spacing, grid = two-column layout
  /**
   * Family group the saved contract bills to today (details edit form). While
   * it stays selected, its name / billing type / members stay editable; any
   * other existing group is used as it is.
   */
  currentFamilyGroupId?: string;
  /**
   * Wizard create flow: select the family account automatically when exactly
   * one account shares a parent with the child and the user has not chosen.
   */
  autoSelectSharedParentAccount?: boolean;
}

export function ContractWizardStepPayers({
  payers,
  setPayers,
  contactsData,
  childId,
  childLastname,
  familyGroup,
  setFamilyGroup,
  showHeader = true,
  layout = "stacked",
  currentFamilyGroupId,
  autoSelectSharedParentAccount = false,
}: ContractWizardStepPayersProps) {
  // Helpers to prevent unnecessary parent updates that can cause render loops
  const arePayersEqual = (a: PayerRequest[], b: PayerRequest[]) => {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      const ap = a[i];
      const bp = b[i];
      if (
        (ap.payer_type || "") !== (bp.payer_type || "") ||
        (ap.contact_id || "") !== (bp.contact_id || "") ||
        (ap.family_group_id || "") !== (bp.family_group_id || "") ||
        (ap.percentage_share || 0) !== (bp.percentage_share || 0)
      ) {
        return false;
      }
    }
    return true;
  };
  // Initialize billing mode derived from data when available; otherwise default to family (wizard default)
  const [billingMode, setBillingMode] = useState<BillingMode>(() => {
    if (payers && payers.length > 0) {
      return payers.some((p) => p.payer_type === "FAMILY_GROUP")
        ? "family"
        : "individual";
    }
    // Only set to family if there's actual family data
    if (familyGroup && (familyGroup.id || familyGroup.members?.length > 0)) {
      return "family";
    }
    return "family"; // Default for new contracts
  });
  const [localFamilyGroup, setLocalFamilyGroup] = useState<FamilyGroup>(() => {
    if (familyGroup) {
      return familyGroup;
    }

    // Surname is filled in by the suggestedFamilyName effect below once the
    // child's parent relationships have loaded. Starting empty avoids racing
    // the effect's "only fill when empty" guard with a stale child-derived
    // placeholder.
    // `familyGroup` is narrowed to `undefined` by the early return above, so
    // `familyGroup?.id` was a read on `never` — spell the value out instead.
    return {
      id: undefined,
      familyName: "",
      billingType: "UNIFIED",
      members: [],
    };
  });

  const buildPayersFromFamily = React.useCallback(
    (fg: FamilyGroup | undefined): PayerRequest[] => {
      if (!fg) return [];
      // An existing family account other than the contract's current one is
      // used as it is: the contract bills 100 % to it. Its billing type and
      // member split stay properties of the group.
      if (fg.id && fg.id !== currentFamilyGroupId) {
        return [
          {
            payer_type: "FAMILY_GROUP",
            family_group_id: fg.id,
            percentage_share: 100,
          } as PayerRequest,
        ];
      }
      if (fg.billingType === "UNIFIED") {
        return [
          { payer_type: "FAMILY_GROUP", percentage_share: 100 } as PayerRequest,
        ];
      }
      if (!fg.members || fg.members.length === 0) return [];
      const total = fg.members.length;
      const equalShare = Math.floor(100 / total);
      const remainder = 100 - equalShare * total;
      return fg.members.map((m, i) => ({
        payer_type: "FAMILY_GROUP",
        contact_id: m.contactId,
        percentage_share:
          m.paymentPercentage ?? equalShare + (i === 0 ? remainder : 0),
      })) as PayerRequest[];
    },
    [currentFamilyGroupId]
  );

  // Initialize parent state on mount when defaults are applied
  const hasInitializedRef = React.useRef(false);
  // Child effects run before this component's mount effect below, so the
  // family-account preselect waits for it; otherwise the mount sync would
  // push the empty initial group over the preselected one.
  const [mountSyncDone, setMountSyncDone] = useState(false);
  useEffect(() => {
    if (!hasInitializedRef.current && billingMode === "family" && setFamilyGroup) {
      // On initial mount with family billing mode, sync the local state to parent
      if (!areFamilyGroupDraftsEqual(familyGroup, localFamilyGroup)) {
        setFamilyGroup(localFamilyGroup);
      }
      // Build and set payers from the initial family group
      const initialPayers = buildPayersFromFamily(localFamilyGroup);
      if (!arePayersEqual(payers, initialPayers)) {
        setPayers(initialPayers);
      }
      hasInitializedRef.current = true;
    }
    setMountSyncDone(true);
    // This effect should only run once on mount, not on subsequent updates
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Track if user explicitly changed the mode so we don't override
  const userSetModeRef = React.useRef(false);

  // Sync incoming familyGroup prop into local state when it changes (avoid loops)
  useEffect(() => {
    if (!familyGroup) return;
    if (!areFamilyGroupDraftsEqual(familyGroup, localFamilyGroup)) {
      setLocalFamilyGroup(familyGroup);
    }
    // Only react to external prop changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [familyGroup]);

  useEffect(() => {
    // Only force to family mode if:
    // 1. familyGroup exists with actual data (members or ID)
    // 2. User hasn't explicitly changed the mode
    if (
      familyGroup &&
      (familyGroup.id || familyGroup.members?.length > 0) &&
      billingMode !== "family" &&
      !userSetModeRef.current
    ) {
      setBillingMode("family");
    }
  }, [familyGroup, billingMode]);

  // Fetch relationships for the selected child to auto-detect parents
  const { data: relationships } = useChildRelationships(
    childId || "",
    !!childId
  );

  // Auto-detect family members from child relationships
  const suggestedFamilyMembers = useMemo(() => {
    if (!relationships || !contactsData) return [];

    // Find all parents and step-parents
    const parentRelationships = relationships.filter(
      (rel) =>
        rel.relationshipType === RelationshipType.PARENT ||
        rel.relationshipType === RelationshipType.STEP_PARENT
    );

    // Map to family members with contact details
    return parentRelationships
      .map((rel) => {
        const contact = contactsData.find((c) => c.id === rel.contactId);
        return {
          contactId: rel.contactId,
          contact,
          relationship: rel.relationshipType,
        };
      })
      .filter((m) => m.contact); // Only include if contact data is found
  }, [relationships, contactsData]);

  // Auto-suggest the bare household surname from the child's parent contacts.
  // The locale-specific "Familie "/"Family "/"Famille " prefix is added at
  // render time by the FE debtor-display helpers — storing it here would
  // produce double-prefix bugs (e.g. "Family Familie X") and would couple
  // the stored value to the child it was generated from. Logic:
  //   * 0 parent contacts available → fall back to the child's lastname so
  //     the wizard still pre-fills *something* sensible while relationships
  //     load (the user can edit before submitting).
  //   * 1 parent → that parent's lastname.
  //   * 2+ parents sharing one surname → the shared surname.
  //   * 2+ parents with distinct surnames → "lastname-mother/lastname-father"
  //     when both parents have a known gender (FEMALE first); alphabetical
  //     otherwise, deterministic for stable UI.
  const suggestedFamilyName = useMemo(() => {
    const parents = suggestedFamilyMembers
      .map((m) => m.contact)
      .filter(
        (c): c is Contact => !!c && !!c.lastname && c.lastname.trim() !== "",
      );

    if (parents.length === 0) {
      return childLastname ? childLastname.trim() : "";
    }

    if (parents.length === 1) {
      return parents[0].lastname!.trim();
    }

    const uniqueSurnames = Array.from(
      new Set(parents.map((p) => p.lastname!.trim())),
    );
    if (uniqueSurnames.length === 1) {
      return uniqueSurnames[0];
    }

    const allHaveGender = parents.every(
      (p) => p.gender === "MALE" || p.gender === "FEMALE",
    );
    const distinctGenders =
      new Set(parents.map((p) => p.gender)).size === parents.length;

    if (allHaveGender && distinctGenders) {
      const ordered = [...parents]
        .sort((a, b) => {
          if (a.gender === b.gender) {
            return a.lastname!.localeCompare(b.lastname!);
          }
          return a.gender === "FEMALE" ? -1 : 1;
        })
        .map((p) => p.lastname!.trim());
      return Array.from(new Set(ordered)).join("/");
    }

    return [...uniqueSurnames]
      .sort((a, b) => a.localeCompare(b))
      .join("/");
  }, [suggestedFamilyMembers, childLastname]);

  // Latest committed local group, for the suggested-name effect below.
  const localFamilyGroupRef = React.useRef(localFamilyGroup);
  useEffect(() => {
    localFamilyGroupRef.current = localFamilyGroup;
  }, [localFamilyGroup]);

  useEffect(() => {
    if (!suggestedFamilyName) return;
    // Read the latest local group from the ref instead of a functional
    // updater: the parent setter below must not run inside an updater, or
    // React warns about updating the parent while rendering this component.
    const prev = localFamilyGroupRef.current;
    if (prev.familyName && prev.familyName.trim().length > 0) return;
    if (prev.familyName === suggestedFamilyName) return;
    const next = { ...prev, familyName: suggestedFamilyName };
    localFamilyGroupRef.current = next;
    setLocalFamilyGroup(next);
    // Propagate to parent so the wizard's Next-button validation (which reads
    // the parent's familyGroup prop) sees the suggested value, and so the
    // [familyGroup] sync-from-parent effect below does not later reset
    // local back to the stale empty parent value on an unrelated re-render.
    if (setFamilyGroup) {
      setFamilyGroup(next);
    }
  }, [suggestedFamilyName, setFamilyGroup]);

  // unified_billing_email is an *optional* override (see Billing Email field
  // below). We intentionally do NOT pre-fill it from a member contact's
  // email_main: snapshotting it here would persist a stale copy that goes
  // out of sync when the parent later edits their contact email, and no UI
  // exists to re-sync it. Leaving it empty is the normal case — invoice
  // email resolution then derives recipients live from the family group's
  // PARENT member contacts at send time
  // (see invoiceEmailService.resolveRecipients).

  // Removed automatic init from suggestions to eliminate mount-time loops.

  // Removed parent-sync effect to avoid render ping-pong; handlers below will update parent explicitly

  // On edit: when payers arrive/changes, set billingMode from data (once, unless user changed it)
  useEffect(() => {
    if (userSetModeRef.current) return;
    if (!payers || payers.length === 0) {
      return;
    }
    const modeFromPayers: BillingMode = payers.some(
      (p) => p.payer_type === "FAMILY_GROUP"
    )
      ? "family"
      : "individual";
    if (modeFromPayers !== billingMode) {
      userSetModeRef.current = false;
      setBillingMode(modeFromPayers);
    }
  }, [payers, billingMode]);

  // Removed passive alignment effect; updates happen only via handlers to avoid loops

  const handleBillingModeChange = (value: string) => {
    if (value === billingMode) return;
    userSetModeRef.current = true;
    setBillingMode(value as BillingMode);
    if (value === "individual") {
      // Clear payers when switching to individual mode
      if (payers.length > 0) setPayers([]);
      // Also clear local family members to avoid accidental re-sync
      setLocalFamilyGroup((prev) => ({ ...prev, members: [] }));
      if (setFamilyGroup) setFamilyGroup(undefined);
    } else if (value === "family") {
      // Ensure we have a family group scaffold so unified can be selected immediately
      setLocalFamilyGroup((prev) => ({
        id: prev.id,
        familyName: prev.familyName || suggestedFamilyName || "",
        billingType: prev.billingType || "UNIFIED",
        members: prev.members || [],
      }));
      if (setFamilyGroup) {
        setFamilyGroup({
          id: familyGroup?.id,
          familyName: familyGroup?.familyName || suggestedFamilyName || "",
          billingType: familyGroup?.billingType || "UNIFIED",
          members: familyGroup?.members || [],
        });
      }
      const next = buildPayersFromFamily(localFamilyGroup);
      if (!arePayersEqual(payers, next)) setPayers(next);
    }
  };

  const handleBillingTypeChange = (type: FamilyBillingType) => {
    const prev = localFamilyGroup;
    const updated: FamilyGroup = { ...prev, billingType: type };
    if (type === "UNIFIED") {
      updated.members = prev.members.map((m) => ({
        ...m,
        paymentPercentage: undefined,
      }));
    } else {
      const equalShare = Math.floor(100 / (prev.members.length || 1));
      const remainder = 100 - equalShare * (prev.members.length || 1);
      updated.members = prev.members.map((m, i) => ({
        ...m,
        paymentPercentage: equalShare + (i === 0 ? remainder : 0),
      }));
    }
    setLocalFamilyGroup(updated);
    if (billingMode === "family") {
      const next = buildPayersFromFamily(updated);
      if (!arePayersEqual(payers, next)) setPayers(next);
    }
    // Push to parent explicitly (no effect)
    if (setFamilyGroup) setFamilyGroup(updated);
  };

  // Removed ensureFamilyPrimary - primary payer no longer needed

  const handleAddFamilyMember = (contactId: string) => {
    const prev = localFamilyGroup;
    const newMember: FamilyMember = { contactId };
    const updated: FamilyGroup =
      prev.billingType === "SPLIT"
        ? (() => {
            const newCount = prev.members.length + 1;
            const equalShare = Math.floor(100 / newCount);
            const remainder = 100 - equalShare * newCount;
            const updatedMembers = prev.members.map((m) => ({
              ...m,
              paymentPercentage: equalShare,
            }));
            newMember.paymentPercentage = equalShare + remainder;
            return { ...prev, members: [...updatedMembers, newMember] };
          })()
        : { ...prev, members: [...prev.members, newMember] };
    setLocalFamilyGroup(updated);
    if (billingMode === "family") {
      const next = buildPayersFromFamily(updated);
      if (!arePayersEqual(payers, next)) setPayers(next);
    }
    if (setFamilyGroup) setFamilyGroup(updated);
  };

  const handleRemoveFamilyMember = (index: number) => {
    const prev = localFamilyGroup;
    const filteredMembers = prev.members.filter((_, i) => i !== index);
    const updated: FamilyGroup = {
      ...prev,
      members:
        prev.billingType === "SPLIT" && filteredMembers.length > 0
          ? (() => {
              const equalShare = Math.floor(100 / filteredMembers.length);
              const remainder = 100 - equalShare * filteredMembers.length;
              return filteredMembers.map((m, i) => ({
                ...m,
                paymentPercentage: equalShare + (i === 0 ? remainder : 0),
              }));
            })()
          : filteredMembers,
    };
    setLocalFamilyGroup(updated);
    if (billingMode === "family") {
      const next = buildPayersFromFamily(updated);
      if (!arePayersEqual(payers, next)) setPayers(next);
    }
    if (setFamilyGroup) setFamilyGroup(updated);
  };

  const handleFamilyMemberPercentageChange = (
    index: number,
    percentage: number
  ) => {
    const prev = localFamilyGroup;
    const updated: FamilyGroup = {
      ...prev,
      members: prev.members.map((m, i) =>
        i === index ? { ...m, paymentPercentage: percentage } : m
      ),
    };
    setLocalFamilyGroup(updated);
    if (billingMode === "family" && updated.billingType === "SPLIT") {
      const next = buildPayersFromFamily(updated);
      if (!arePayersEqual(payers, next)) setPayers(next);
    }
    if (setFamilyGroup) setFamilyGroup(updated);
  };

  const autoDistributePercentages = () => {
    if (localFamilyGroup.members.length === 0) return;

    const equalShare = Math.floor(100 / localFamilyGroup.members.length);
    const remainder = 100 - equalShare * localFamilyGroup.members.length;

    setLocalFamilyGroup((prev) => ({
      ...prev,
      members: prev.members.map((m, i) => ({
        ...m,
        paymentPercentage: equalShare + (i === 0 ? remainder : 0),
      })),
    }));
  };

  useEffect(() => {
    if (billingMode !== "family" || localFamilyGroup.members.length === 0) {
      return;
    }

    // Removed primary payer check
  }, [billingMode, localFamilyGroup.members]);

  // Removed handleSetPrimaryFamilyMember - primary payer no longer needed

  // Get available contacts for adding to family (exclude already added)
  const availableContacts = useMemo(() => {
    if (!contactsData) return [];
    const memberIds = new Set(localFamilyGroup.members.map((m) => m.contactId));
    // `models_Contact.id` is optional, while `FamilyMember.contactId` is
    // required — a contact without an id could never be added, so narrow here
    // instead of pushing `undefined` into the family group.
    return contactsData.filter(
      (c): c is Contact & { id: string } =>
        c.id !== undefined && !memberIds.has(c.id)
    );
  }, [contactsData, localFamilyGroup.members]);

  const familyPercentagesValid = useMemo(() => {
    if (localFamilyGroup.billingType === "UNIFIED") return true;
    const total = localFamilyGroup.members.reduce(
      (sum, m) => sum + (m.paymentPercentage || 0),
      0
    );
    return total === 100;
  }, [localFamilyGroup]);

  // The contract's current family group as first loaded (details edit form),
  // so selecting it again after looking at another account restores its
  // editable settings instead of the picker row's subset.
  const currentGroupSnapshotRef = React.useRef<FamilyGroup | undefined>(
    undefined
  );
  useEffect(() => {
    if (
      currentFamilyGroupId &&
      familyGroup?.id === currentFamilyGroupId &&
      currentGroupSnapshotRef.current?.id !== currentFamilyGroupId
    ) {
      currentGroupSnapshotRef.current = familyGroup;
    }
  }, [currentFamilyGroupId, familyGroup]);

  const applyFamilyGroup = (updated: FamilyGroup) => {
    setLocalFamilyGroup(updated);
    if (setFamilyGroup) setFamilyGroup(updated);
    const next = buildPayersFromFamily(updated);
    if (!arePayersEqual(payers, next)) setPayers(next);
  };

  const handleSelectExistingAccount = (
    item: FamilyGroupPickerItem,
    auto: boolean
  ) => {
    // The automatic preselect is not a user choice: a later remount of the
    // step may still preselect, and the user's own choice always wins.
    const accountChoiceMade = auto ? localFamilyGroup.accountChoiceMade : true;
    if (item.id === currentFamilyGroupId && currentGroupSnapshotRef.current) {
      applyFamilyGroup({
        ...currentGroupSnapshotRef.current,
        accountChoiceMade,
      });
      return;
    }
    applyFamilyGroup({
      id: item.id,
      familyName: item.familyName,
      billingType: String(item.billingType) === "SPLIT" ? "SPLIT" : "UNIFIED",
      unifiedBillingEmail: item.unifiedBillingEmail,
      members: item.members.map((m) => ({ contactId: m.contactId })),
      accountChoiceMade,
    });
  };

  const handleCreateNewAccount = () => {
    if (!localFamilyGroup.id) {
      if (!localFamilyGroup.accountChoiceMade) {
        applyFamilyGroup({ ...localFamilyGroup, accountChoiceMade: true });
      }
      return;
    }
    applyFamilyGroup({
      id: undefined,
      familyName: suggestedFamilyName || "",
      billingType: "UNIFIED",
      members: [],
      accountChoiceMade: true,
    });
  };

  // An existing family account other than the contract's current one is used
  // as it is, so its name, billing type and members are not edited here.
  const usesOtherExistingAccount =
    !!localFamilyGroup.id && localFamilyGroup.id !== currentFamilyGroupId;
  const showGroupSettings = !usesOtherExistingAccount;

  return (
    <>
      {showHeader && (
        <div className="mb-6">
          <h3 className="text-lg font-medium mb-2">
            <Trans id="contracts.wizard.payers.title">
              Payment Configuration
            </Trans>
          </h3>
          <p className="text-sm text-muted-foreground">
            <Trans id="contracts.wizard.payers.description">
              Choose how billing should be handled for this contract
            </Trans>
          </p>
        </div>
      )}

      <div
        className={layout === "grid" ? "grid grid-cols-2 gap-6" : "space-y-6"}
      >
        {/* Billing Mode Selection */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              <Trans>Billing Mode</Trans>
            </CardTitle>
            <CardDescription>
              <Trans>
                Select whether to use family-based or individual billing
              </Trans>
            </CardDescription>
          </CardHeader>
          <CardContent>
            <RadioGroup
              value={billingMode}
              onValueChange={handleBillingModeChange}
              name="billing-mode"
            >
              <div className="flex items-start space-x-3 mb-3">
                <RadioGroupItem
                  value="family"
                  id="billing-mode-family"
                  className="mt-1"
                />
                <Label htmlFor="billing-mode-family" className="cursor-pointer">
                  <div className="flex items-start gap-2">
                    <Users className="h-4 w-4 mt-0.5 text-muted-foreground" />
                    <div>
                      <div className="font-medium">
                        <Trans>Family Billing</Trans>
                      </div>
                      <div className="text-sm text-muted-foreground">
                        <Trans>
                          Group multiple family members for consolidated billing
                        </Trans>
                      </div>
                    </div>
                  </div>
                </Label>
              </div>

              <div className="flex items-start space-x-3">
                <RadioGroupItem
                  value="individual"
                  id="billing-mode-individual"
                  className="mt-1"
                />
                <Label
                  htmlFor="billing-mode-individual"
                  className="cursor-pointer"
                >
                  <div className="flex items-start gap-2">
                    <User className="h-4 w-4 mt-0.5 text-muted-foreground" />
                    <div>
                      <div className="font-medium">
                        <Trans>Individual Recipients</Trans>
                      </div>
                      <div className="text-sm text-muted-foreground">
                        <Trans>
                          Select specific contacts as individual payers
                        </Trans>
                      </div>
                    </div>
                  </div>
                </Label>
              </div>
            </RadioGroup>
          </CardContent>
        </Card>

        {/* Family Billing Configuration */}
        {billingMode === "family" && (
          <>
            {/* Family Details */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  <Trans>Family Configuration</Trans>
                </CardTitle>
                <CardDescription>
                  <Trans>Configure the family group for billing</Trans>
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <FamilyAccountChooser
                  childId={childId}
                  selectedId={localFamilyGroup.id}
                  selectedName={localFamilyGroup.familyName}
                  autoPreselect={
                    autoSelectSharedParentAccount &&
                    mountSyncDone &&
                    !localFamilyGroup.id &&
                    !localFamilyGroup.accountChoiceMade
                  }
                  onSelectExisting={handleSelectExistingAccount}
                  onCreateNew={handleCreateNewAccount}
                />

                {usesOtherExistingAccount && (
                  <p className="text-sm text-muted-foreground">
                    <Trans id="contracts.familyAccount.existingNote">
                      This contract bills to the selected family account. The
                      account's name, billing type and members stay as they are.
                    </Trans>
                  </p>
                )}

                {showGroupSettings && (
                  <FamilyGroupSettingsFields
                    group={localFamilyGroup}
                    suggestedFamilyName={suggestedFamilyName}
                    onChange={(next) => {
                      setLocalFamilyGroup(next);
                      // Mirror to parent so Next-button validation sees the
                      // typed value (the parent prop is the source of truth
                      // for the wizard's overall validity check).
                      if (setFamilyGroup) setFamilyGroup(next);
                    }}
                    onBillingTypeChange={handleBillingTypeChange}
                  />
                )}
              </CardContent>
            </Card>

            {/* Family Members */}
            {showGroupSettings && localFamilyGroup.billingType === "SPLIT" && (
              <FamilyMembersCard
                members={localFamilyGroup.members}
                contacts={contactsData}
                suggestedMembers={suggestedFamilyMembers}
                availableContacts={availableContacts}
                onAddMember={handleAddFamilyMember}
                onRemoveMember={handleRemoveFamilyMember}
                onPercentageChange={handleFamilyMemberPercentageChange}
                onDistributeEqually={autoDistributePercentages}
              />
            )}

            {/* Validation Messages */}
            {showGroupSettings &&
              localFamilyGroup.billingType === "SPLIT" &&
              localFamilyGroup.members.length === 0 && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    <Trans>Please add at least one family member</Trans>
                  </AlertDescription>
                </Alert>
              )}

            {showGroupSettings &&
              localFamilyGroup.billingType === "SPLIT" &&
              !familyPercentagesValid &&
              localFamilyGroup.members.length > 0 && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    <Trans>
                      Payment percentages must add up to exactly 100%
                    </Trans>
                  </AlertDescription>
                </Alert>
              )}

            {showGroupSettings && !localFamilyGroup.familyName && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  <Trans>Please enter a family name</Trans>
                </AlertDescription>
              </Alert>
            )}
          </>
        )}

        {/* Individual Payers */}
        {billingMode === "individual" && (
          <>
            <PayerSplitEditor
              payers={payers}
              onPayersChange={setPayers}
              contacts={contactsData || []}
            />

            {payers.length > 0 && !validatePayerPercentages(payers) && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  <Trans id="contracts.wizard.validation.payerPercentages">
                    Payment percentages must add up to exactly 100%
                  </Trans>
                </AlertDescription>
              </Alert>
            )}
          </>
        )}
      </div>
    </>
  );
}
